import {
  Check as CheckIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  ContentCopy as ContentCopyIcon,
  CreditCard as CreditCardIcon,
  Edit as EditIcon,
  ErrorOutline as ErrorOutlineIcon,
  Notes as NotesIcon,
  Payments as PaymentsIcon,
  Person as PersonIcon,
  PhoneAndroid as PhoneAndroidIcon,
  Replay as ReplayIcon,
  Restaurant as RestaurantIcon,
  TakeoutDining as TakeoutDiningIcon,
  Timer as TimerIcon,
} from '../icons';
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Divider,
  CircularProgress,
  Paper,
  IconButton,
  Alert,
  useMediaQuery,
  LinearProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import { useCart } from '../../contexts/CartContext';
import MenuItemDetailsDialog from '../menu/MenuItemDetailsDialog';
import { usePublicTheme } from '../../contexts/PublicThemeContext';
import CurrencyDisplay from '../currency/CurrencyDisplay';
import { useCurrencyFormatter } from '../../contexts/CurrencyContext';
import orderService from '../../services/orderService';
import paymentService from '../../services/paymentService';
import menuService from '../../services/menuService';
import { getHoverColor, hexToRgba } from '../../utils/colorUtils';

const steps = ['Order Type', 'Review Order', 'Customer Information', 'Payment Method', 'Confirmation'];

const OrderTypeCard = styled(Box)(({ theme, isSelected, primaryColor }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3.5),
  background: isSelected
    ? `linear-gradient(135deg, ${hexToRgba(primaryColor || '#667eea', 0.1)} 0%, ${hexToRgba(primaryColor || '#667eea', 0.05)} 100%)`
    : 'rgba(255, 255, 255, 0.95)',
  border: isSelected
    ? `2px solid ${primaryColor || '#667eea'}`
    : '1px solid rgba(0, 0, 0, 0.05)',
  boxShadow: isSelected
    ? `0 10px 30px ${hexToRgba(primaryColor || '#667eea', 0.2)}, 0 4px 12px rgba(0, 0, 0, 0.08)`
    : '0 10px 30px rgba(0, 0, 0, 0.08)',
  backdropFilter: 'blur(10px)',
  cursor: 'pointer',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  flex: 1,
  minHeight: 200,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2.5),
    borderRadius: theme.spacing(2.5),
    minHeight: 160,
  },
  '&:hover': {
    backgroundColor: isSelected
      ? `rgba(255, 255, 255, 1)`
      : 'rgba(255, 255, 255, 1)',
    transform: 'translateY(-10px) scale(1.03)',
    boxShadow: isSelected
      ? `0 25px 60px ${hexToRgba(primaryColor || '#667eea', 0.3)}, 0 10px 25px rgba(0, 0, 0, 0.12)`
      : '0 25px 60px rgba(0, 0, 0, 0.18), 0 10px 25px rgba(0, 0, 0, 0.12)',
    borderColor: primaryColor || '#667eea',
    '& .order-type-icon': {
      transform: 'scale(1.1)',
      color: primaryColor || '#667eea',
    },
    [theme.breakpoints.down('sm')]: {
      transform: 'translateY(-5px) scale(1.02)',
    },
  },
  '&:active': {
    transform: 'translateY(-6px) scale(1.015)',
    [theme.breakpoints.down('sm')]: {
      transform: 'translateY(-3px) scale(1.01)',
    },
  },
}));

const CheckmarkBadge = styled(Box)(({ theme, primaryColor }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  width: 32,
  height: 32,
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${primaryColor || '#667eea'} 0%, ${getHoverColor(primaryColor || '#667eea', 'darken', 15)} 100%)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: `0 4px 14px ${hexToRgba(primaryColor || '#667eea', 0.4)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}));

const IconContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
}));

const CheckoutDialog = ({ open, onClose, onTrackOrder }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { items, merchant, table, clearCart, setMerchantAndTable, orderBreakdown, updateItem } = useCart();
  const { primaryColor } = usePublicTheme();
  const { formatCurrency } = useCurrencyFormatter();
  const [freshMerchant, setFreshMerchant] = useState(null);

  // Helper function to calculate item price including options
  const calculateItemPrice = (item) => {
    let itemPrice = parseFloat(item.price) || 0;

    if (item.options && item.options.length > 0) {
      item.options.forEach(option => {
        const optionPrice = parseFloat(option.price_addition) || 0;
        itemPrice += optionPrice;
      });
    }

    return itemPrice;
  };
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState(null);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    orderType: '',
    name: '',
    notes: '',
    paymentMethod: ''
  });
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [autoAppliedOptions, setAutoAppliedOptions] = useState({});

  // GCash payment state
  const [gcashInfo, setGcashInfo] = useState(null);
  const [gcashUploading, setGcashUploading] = useState(false);
  const [gcashResult, setGcashResult] = useState(null);
  const [gcashTimeLeft, setGcashTimeLeft] = useState(null);
  const [gcashExpired, setGcashExpired] = useState(false);
  // 'breakdown' shows the bill summary first; 'payment' shows the send/upload screen.
  const [gcashStage, setGcashStage] = useState('breakdown');
  const [gcashSelectedFile, setGcashSelectedFile] = useState(null);
  const [gcashPreviewUrl, setGcashPreviewUrl] = useState(null);
  const [gcashNumberCopied, setGcashNumberCopied] = useState(false);

  const GCASH_RECEIPT_ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const GCASH_RECEIPT_MAX_BYTES = 10 * 1024 * 1024;

  /** @returns {string | null} a customer-facing error, or null when the file is acceptable */
  const validateReceiptFile = (file) => {
    if (!file) return 'Please select a receipt screenshot first.';
    if (!GCASH_RECEIPT_ALLOWED_TYPES.includes(file.type)) {
      return 'Invalid file type. Please upload a JPG, PNG, or WebP image.';
    }
    if (file.size > GCASH_RECEIPT_MAX_BYTES) {
      return 'File is too large. Maximum size is 10MB.';
    }
    return null;
  };

  const GCASH_SESSION_KEY = 'merchqry_gcash_payment';

  const effectiveMerchantId = () => (freshMerchant || merchant)?.id ?? null;

  const saveGcashSession = (session) => {
    try {
      sessionStorage.setItem(GCASH_SESSION_KEY, JSON.stringify(session));
    } catch (_) { /* storage full or unavailable */ }
  };

  const clearGcashSession = () => {
    try { sessionStorage.removeItem(GCASH_SESSION_KEY); } catch (_) {}
  };

  /** @returns {{ paymentId: string, merchantId: string, order: { id: string, order_number?: string, status?: string } } | null} */
  const loadGcashSession = () => {
    try {
      const raw = sessionStorage.getItem(GCASH_SESSION_KEY);
      if (!raw) return null;
      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch {
        clearGcashSession();
        return null;
      }
      if (!parsed || typeof parsed !== 'object' || !parsed.paymentId || !parsed.merchantId) {
        return null;
      }
      const id = effectiveMerchantId();
      if (!id || parsed.merchantId !== id) {
        return null;
      }
      return parsed;
    } catch (_) {
      return null;
    }
  };

  useEffect(() => {
    if (formData.orderType !== 'takeout') {
      setAutoAppliedOptions({});
      return;
    }

    const applied = {};
    items.forEach((item, idx) => {
      const meta = item._optionGroupsMeta || [];
      const perTableGroups = meta.filter(g => g.requirement_type === 'PER_TABLE');
      if (perTableGroups.length === 0) return;

      const currentOptions = item.options || [];
      const missing = perTableGroups.filter(
        g => !currentOptions.some(opt => {
          const groupItemIds = (g.items || []).map(i => i.id);
          return groupItemIds.includes(opt.id);
        })
      );

      if (missing.length > 0) {
        applied[idx] = missing
          .map(g => {
            const firstItem = (g.items || [])[0];
            if (!firstItem) return null;
            return { ...firstItem, _groupId: g.id, _autoApplied: true };
          })
          .filter(Boolean);
      }
    });

    setAutoAppliedOptions(applied);
  }, [formData.orderType, items]);

  const getEffectiveItems = () => {
    if (formData.orderType !== 'takeout' || Object.keys(autoAppliedOptions).length === 0) {
      return items;
    }
    return items.map((item, idx) => {
      const extra = autoAppliedOptions[idx];
      if (!extra || extra.length === 0) return item;
      return { ...item, options: [...(item.options || []), ...extra] };
    });
  };

  const autoAppliedExtra = Object.entries(autoAppliedOptions).reduce((total, [idx, opts]) => {
    const item = items[parseInt(idx)];
    if (!item) return total;
    const optExtra = opts.reduce((s, o) => s + (parseFloat(o.price_addition) || 0), 0);
    return total + optExtra * (item.quantity || 1);
  }, 0);

  const adjustedBreakdown = (() => {
    if (autoAppliedExtra === 0) return orderBreakdown;
    const subtotal = orderBreakdown.subtotal + autoAppliedExtra;
    const settings = merchant?.settings || {};
    const taxPct = parseFloat(settings.tax_percentage) || 0;
    const serviceChargePct = parseFloat(settings.service_charge_percentage) || 0;

    let totalDiscount = 0;
    const discountDetails = (orderBreakdown.discountDetails || []).map((d) => {
      const val = Math.max(parseFloat(d.value) || 0, 0);
      let amount = 0;
      if (d.discount_type === 'percentage') {
        amount = subtotal * (val / 100);
      } else {
        amount = val;
      }
      amount = Math.min(amount, subtotal);
      totalDiscount += amount;
      return { ...d, amount };
    });
    totalDiscount = Math.min(totalDiscount, subtotal);

    const afterDiscount = Math.max(subtotal - totalDiscount, 0);
    const tax = afterDiscount * (taxPct / 100);
    const serviceCharge = afterDiscount * (serviceChargePct / 100);
    const platformFee = parseFloat(settings.platform_service_fee) || 0;
    return {
      ...orderBreakdown,
      subtotal,
      discountDetails,
      totalDiscount,
      tax,
      serviceCharge,
      platformFee,
      total: afterDiscount + tax + serviceCharge + platformFee,
    };
  })();

  const getDefaultOrderType = (merchantData) => {
    const settings = merchantData?.settings;
    if (!settings) return 'dine_in';
    if (settings.enable_dine_in !== false) return 'dine_in';
    if (settings.enable_takeout) return 'takeout';
    return '';
  };

  const getDefaultPaymentMethod = (merchantData) => {
    const settings = merchantData?.settings;
    if (!settings) return '';
    if (settings.enable_cash) return 'cash';
    if (settings.gcash_enabled) return 'gcash';
    return '';
  };

  const getAllowedPaymentMethodValues = (merchantData) => {
    if (!merchantData?.settings) return [];
    const values = [];
    if (merchantData.settings.enable_cash) values.push('cash');
    if (merchantData.settings.gcash_enabled) values.push('gcash');
    return values;
  };

  const resolvePaymentMethodForMerchant = (selected, merchantData) => {
    const allowed = getAllowedPaymentMethodValues(merchantData);
    if (allowed.length === 0) return '';
    if (selected && allowed.includes(selected)) return selected;
    const nextDefault = getDefaultPaymentMethod(merchantData);
    if (nextDefault && allowed.includes(nextDefault)) return nextDefault;
    return allowed[0];
  };

  const paymentMerchant = freshMerchant || merchant;
  const resolvedPaymentMethod = resolvePaymentMethodForMerchant(
    formData.paymentMethod,
    paymentMerchant
  );

  const isGcashPaymentActive = () => {
    return (
      resolvedPaymentMethod === 'gcash' &&
      activeStep === 4 &&
      gcashInfo &&
      !gcashResult?.is_verified &&
      !gcashExpired
    );
  };

  const resetDialogState = () => {
    setActiveStep(0);
    setLoading(false);
    setError(null);
    setOrder(null);
    setFreshMerchant(null);
    setEditingItemIndex(null);
    setFormData({
      orderType: getDefaultOrderType(merchant),
      name: '',
      notes: '',
      paymentMethod: getDefaultPaymentMethod(merchant),
    });
    setGcashInfo(null);
    setGcashUploading(false);
    setGcashResult(null);
    setGcashTimeLeft(null);
    setGcashExpired(false);
    setGcashStage('breakdown');
    setGcashSelectedFile(null);
    setGcashNumberCopied(false);
    clearGcashSession();
  };

  const prevOpenRef = React.useRef(false);
  const [gcashRestoring, setGcashRestoring] = useState(false);

  useEffect(() => {
    if (!open) {
      prevOpenRef.current = false;
      return;
    }
    if (!effectiveMerchantId()) {
      return;
    }
    if (prevOpenRef.current) {
      return;
    }

    const session = loadGcashSession();
    if (session?.paymentId) {
      let cancelled = false;
      setGcashRestoring(true);
      prevOpenRef.current = true;
      paymentService.getGcashPaymentInfo(session.paymentId)
        .then((data) => {
          if (cancelled) return;
          if (data.status === 'pending' || data.status === 'processing' || data.status === 'failed') {
            const expiry = data.expires_at ? new Date(data.expires_at).getTime() : 0;
            if (expiry > Date.now()) {
              setGcashInfo(data);
              setGcashResult(null);
              setGcashExpired(false);
              setGcashTimeLeft(null);
              setGcashStage('breakdown');
              setGcashSelectedFile(null);
              setFormData(prev => ({ ...prev, paymentMethod: 'gcash' }));
              setActiveStep(4);
              const oid = data.order_id || session.order?.id;
              if (oid) {
                setOrder({
                  id: oid,
                  order_number: session.order?.order_number ?? data.order_number,
                  status: session.order?.status ?? data.order_status,
                });
              }
            } else {
              clearGcashSession();
              resetDialogState();
            }
          } else {
            clearGcashSession();
            resetDialogState();
          }
        })
        .catch(() => {
          if (!cancelled) {
            clearGcashSession();
            resetDialogState();
          }
        })
        .finally(() => {
          if (!cancelled) setGcashRestoring(false);
        });
      return () => { cancelled = true; };
    }

    resetDialogState();
    prevOpenRef.current = true;
  }, [open, merchant?.id, freshMerchant?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch fresh merchant data in the background when dialog opens
  useEffect(() => {
    const fetchFreshMerchantData = async (merchantId) => {
      try {
        const menuData = await menuService.getPublicMenu(merchantId);
        setFreshMerchant(menuData.merchant);
        setMerchantAndTable(menuData.merchant, table);

        const freshSettings = menuData.merchant?.settings;
        if (freshSettings?.status === 'paused') {
          setError('This restaurant is currently closed. Please try again later.');
          return;
        }
        if (freshSettings?.is_currently_open === false) {
          setError('This restaurant is currently outside business hours. Please try again during operating hours.');
          return;
        }

        // Update defaults if the fresh data differs from the stale cart data
        setFormData(prev => {
          const freshOrderType = getDefaultOrderType(menuData.merchant);
          const updates = {};

          if (freshOrderType && prev.orderType !== freshOrderType) {
            // Only override if the user hasn't manually changed it yet
            const staleDefault = getDefaultOrderType(merchant);
            if (prev.orderType === staleDefault || prev.orderType === '') {
              updates.orderType = freshOrderType;
            }
          }

          const resolvedPayment = resolvePaymentMethodForMerchant(
            prev.paymentMethod,
            menuData.merchant
          );
          if (resolvedPayment !== prev.paymentMethod) {
            updates.paymentMethod = resolvedPayment;
          }

          return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
        });
      } catch (err) {
        console.error('Error fetching fresh merchant data:', err);
        setError('Failed to load merchant settings. Please try again.');
      }
    };

    if (open && merchant?.id) {
      fetchFreshMerchantData(merchant.id);
    }
  }, [open, merchant?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- setMerchantAndTable and table omitted to prevent infinite loop

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCancelGcash = () => {
    // Guard: never cancel mid-verification.
    if (gcashUploading) {
      return;
    }
    const paymentId = gcashInfo?.payment_id ?? gcashInfo?.paymentId;
    clearGcashSession();
    // State is reset on the dialog's exit transition (onExited) so the closing
    // animation doesn't briefly flash the first step.
    onClose();
    if (paymentId) {
      void Promise.resolve()
        .then(() => paymentService.cancelGcashPayment(paymentId))
        .catch(() => {
          // best-effort server cancel; dialog already closed
        });
    }
  };

  const handleClose = () => {
    if (isGcashPaymentActive()) {
      return;
    }
    // Reset happens on the exit transition (onExited), not synchronously, to
    // avoid a flash of step 1 while the dialog animates closed.
    onClose();
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleClose();
      return;
    }

    if (activeStep === 0) {
      // Validate order type is selected
      if (!formData.orderType) {
        setError('Please select an order type.');
        return;
      }
    } else if (activeStep === 1) {
      // Validate cart is not empty
      if (items.length === 0) {
        setError('Your cart is empty. Please add items to continue.');
        return;
      }
    } else if (activeStep === 2) {
      // Validate customer information
      if (!formData.name) {
        setError('Please provide your name.');
        return;
      }
    } else if (activeStep === 3) {
      if (!resolvedPaymentMethod) {
        setError('Please select a payment method.');
        return;
      }
      if (resolvedPaymentMethod !== formData.paymentMethod) {
        setFormData(prev => ({ ...prev, paymentMethod: resolvedPaymentMethod }));
      }

      createOrder(resolvedPaymentMethod);
      return; // Don't proceed until order creation is complete
    }

    setActiveStep(prevStep => prevStep + 1);
    setError(null);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      // If on first step, close the dialog
      handleClose();
    } else {
      setActiveStep(prevStep => prevStep - 1);
      setError(null);
    }
  };

  const isMerchantClosed = () => {
    const currentMerchant = freshMerchant || merchant;
    const settings = currentMerchant?.settings;
    if (!settings) return false;
    return settings.status === 'paused' || settings.is_currently_open === false;
  };

  const isNextDisabled = () => {
    if (loading) return true;

    if (isMerchantClosed()) return true;

    // On order type step, disable if no order type is selected
    if (activeStep === 0) {
      const currentMerchant = freshMerchant || merchant;
      if (!currentMerchant?.settings) return true; // Still loading

      const enableDineIn = currentMerchant.settings.enable_dine_in !== undefined
        ? currentMerchant.settings.enable_dine_in
        : true;
      const enableTakeout = currentMerchant.settings.enable_takeout !== undefined
        ? currentMerchant.settings.enable_takeout
        : true;

      // If both are disabled, disable the button
      if (!enableDineIn && !enableTakeout) return true;

      // If no order type is selected, disable the button
      if (!formData.orderType) return true;
    }

    return false;
  };

  const createOrder = async (explicitPaymentMethod) => {
    try {
      setLoading(true);
      setError(null);

      const currentMerchant = freshMerchant || merchant;
      const paymentMethod =
        explicitPaymentMethod !== undefined && explicitPaymentMethod !== null && explicitPaymentMethod !== ''
          ? explicitPaymentMethod
          : resolvePaymentMethodForMerchant(formData.paymentMethod, currentMerchant);

      if (!paymentMethod) {
        setError('Please select a payment method.');
        setLoading(false);
        return;
      }
      if (paymentMethod !== formData.paymentMethod) {
        setFormData(prev => ({ ...prev, paymentMethod }));
      }

      const effectiveItems = getEffectiveItems();
      const orderData = {
        table_id: table ? table.id : null,
        customer_name: formData.name,
        notes: formData.notes,
        payment_method: paymentMethod,
        order_type: formData.orderType,
        items: effectiveItems.map(item => ({
          menu_item_id: item.id,
          quantity: item.quantity,
          notes: item.notes || '',
          options: item.options ? item.options.map(option => option.id) : []
        }))
      };

      // Create order
      const createdOrder = await orderService.createPublicOrder(orderData);
      setOrder(createdOrder);

      // Handle different payment methods
      if (paymentMethod === 'gcash') {
        try {
          const gcashData = createdOrder.gcash_info;
          if (gcashData) {
            setGcashInfo(gcashData);
            setGcashResult(null);
            setGcashExpired(false);
            setGcashStage('breakdown');
            setGcashSelectedFile(null);
            setActiveStep(4);
            saveGcashSession({
              paymentId: gcashData.payment_id,
              merchantId: (freshMerchant || merchant)?.id,
              order: {
                id: createdOrder.id,
                order_number: createdOrder.order_number,
                status: createdOrder.status,
              },
            });
          } else {
            throw new Error('GCash payment info not received');
          }
        } catch (paymentError) {
          console.error('Error setting up GCash payment:', paymentError);
          setError('Failed to set up GCash payment. Please try again.');
          setLoading(false);
          return;
        }
      } else {
        setActiveStep(4);
        clearCart();
      }

      setLoading(false);
    } catch (error) {
      console.error('Error creating order:', error);
      // Try to extract error message from response
      const errorMessage = error.response?.data?.detail ||
                          error.response?.data?.message ||
                          error.message ||
                          'Failed to create order. Please try again.';
      setError(errorMessage);
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderOrderType();
      case 1:
        return renderOrderReview();
      case 2:
        return renderCustomerInfo();
      case 3:
        return renderPaymentMethod();
      case 4:
        if (resolvedPaymentMethod === 'gcash' && gcashInfo && !gcashResult?.is_verified) {
          return renderGcashPayment();
        }
        return renderConfirmation();
      default:
        return null;
    }
  };

  const renderOrderType = () => {
    // Use fresh merchant data if available, fallback to cart merchant data
    const currentMerchant = freshMerchant || merchant;

    // Show loading state if merchant settings are not yet loaded
    if (!currentMerchant?.settings) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Loading order options...
          </Typography>
        </Box>
      );
    }

    // Build order type options dynamically based on merchant settings
    const orderTypeOptions = [];

    // Add Dine-In if enabled (default to true if not set)
    const enableDineIn = currentMerchant.settings.enable_dine_in !== undefined
      ? currentMerchant.settings.enable_dine_in
      : true;
    const enableTakeout = currentMerchant.settings.enable_takeout !== undefined
      ? currentMerchant.settings.enable_takeout
      : true;

    if (enableDineIn) {
      orderTypeOptions.push({
        value: 'dine_in',
        label: 'Dine-In',
        description: 'Eat at the restaurant',
      });
    }

    // Add Takeout if enabled
    if (enableTakeout) {
      orderTypeOptions.push({
        value: 'takeout',
        label: 'Takeout',
        description: 'Take away for pickup',
      });
    }

    // If no order type options are available, show an error
    if (orderTypeOptions.length === 0) {
      return (
        <Box>
          <Typography variant="h6" gutterBottom>
            Order Type
          </Typography>
          <Alert severity="error" sx={{ mt: 2 }}>
            No order types are currently available. Please contact the restaurant.
          </Alert>
        </Box>
      );
    }

    return (
      <Box>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 800,
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            mb: { xs: 2, sm: 3 },
            letterSpacing: '-0.02em',
            color: '#1f2937',
          }}
        >
          Select Order Type
        </Typography>

        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 3 },
          mt: 2
        }}>
          {orderTypeOptions.map((option) => {
            const isSelected = formData.orderType === option.value;
            const IconComponent = option.value === 'dine_in' ? RestaurantIcon : TakeoutDiningIcon;

            return (
              <OrderTypeCard
                key={option.value}
                isSelected={isSelected}
                primaryColor={primaryColor}
                onClick={() => setFormData(prev => ({ ...prev, orderType: option.value }))}
              >
                {isSelected && (
                  <CheckmarkBadge primaryColor={primaryColor}>
                    <CheckIcon sx={{ color: 'white', fontSize: { xs: 16, sm: 18 } }} />
                  </CheckmarkBadge>
                )}
                <IconContainer className="order-type-icon">
                  <IconComponent
                    sx={{
                      fontSize: { xs: 48, sm: 64 },
                      color: isSelected ? (primaryColor || '#667eea') : '#6b7280',
                      transition: 'color 0.3s ease',
                    }}
                  />
                </IconContainer>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 800,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                    mb: 1,
                    letterSpacing: '-0.02em',
                    color: isSelected ? (primaryColor || '#667eea') : '#1f2937',
                    transition: 'color 0.3s ease',
                  }}
                >
                  {option.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#6b7280',
                    textAlign: 'center',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                  }}
                >
                  {option.description}
                </Typography>
              </OrderTypeCard>
            );
          })}
        </Box>
      </Box>
    );
  };

  const handleEditItem = (index) => {
    setEditingItemIndex(index);
  };

  const handleUpdateItem = (index, updatedFields) => {
    updateItem(index, updatedFields);
    setEditingItemIndex(null);
  };

  const renderOrderReview = () => (
    <Box>
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontWeight: 800,
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          letterSpacing: '-0.02em',
          color: '#1f2937',
          mb: 0.5,
        }}
      >
        Review Your Order
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          color: '#9ca3af',
          fontSize: { xs: '0.8rem', sm: '0.875rem' },
          mb: 3,
        }}
      >
        Tap an item to edit options or special instructions
      </Typography>

      {/* Order Type Badge */}
      <Box sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        mb: 2.5,
        borderRadius: 3,
        background: `linear-gradient(135deg, ${hexToRgba(primaryColor || '#667eea', 0.1)} 0%, ${hexToRgba(primaryColor || '#667eea', 0.05)} 100%)`,
        border: `1.5px solid ${hexToRgba(primaryColor || '#667eea', 0.2)}`,
      }}>
        {formData.orderType === 'dine_in'
          ? <RestaurantIcon sx={{ fontSize: '1rem', color: primaryColor || '#667eea' }} />
          : <TakeoutDiningIcon sx={{ fontSize: '1rem', color: primaryColor || '#667eea' }} />}
        <Typography
          variant="body2"
          sx={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 700,
            color: primaryColor || '#667eea',
            fontSize: '0.85rem',
          }}
        >
          {formData.orderType === 'dine_in' ? 'Dine-In' : formData.orderType === 'takeout' ? 'Takeout' : 'Not selected'}
        </Typography>
      </Box>

      {/* Items List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
        {getEffectiveItems().map((item, index) => (
          <Paper
            key={`${item.id}-${index}`}
            role="button"
            tabIndex={0}
            aria-label={`Edit ${item.name || 'item'}`}
            onClick={() => handleEditItem(index)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                if (e.key === ' ') e.preventDefault();
                handleEditItem(index);
              }
            }}
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderRadius: 3,
              border: '1.5px solid rgba(0, 0, 0, 0.06)',
              cursor: 'pointer',
              outline: 'none',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                borderColor: hexToRgba(primaryColor || '#667eea', 0.3),
                boxShadow: `0 4px 16px ${hexToRgba(primaryColor || '#667eea', 0.1)}`,
                transform: 'translateY(-1px)',
              },
              '&:focus-visible': {
                borderColor: hexToRgba(primaryColor || '#667eea', 0.5),
                boxShadow: `0 0 0 2px ${hexToRgba(primaryColor || '#667eea', 0.35)}`,
              },
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Chip
                    label={`${item.quantity}x`}
                    size="small"
                    sx={{
                      height: 24,
                      minWidth: 36,
                      fontWeight: 800,
                      fontSize: '0.75rem',
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      background: `linear-gradient(135deg, ${primaryColor || '#667eea'} 0%, ${getHoverColor(primaryColor || '#667eea', 'darken', 15)} 100%)`,
                      color: '#fff',
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontWeight: 700,
                      color: '#1f2937',
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                    }}
                  >
                    {item.name}
                  </Typography>
                </Box>
                {item.options && item.options.length > 0 && (
                  <Box sx={{ ml: 5.5, display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                    {item.options.map(option => {
                      const priceLabel = parseFloat(option.price_addition) > 0
                        ? ` +${formatCurrency(parseFloat(option.price_addition))}`
                        : '';
                      return (
                        <Chip
                          key={option.id}
                          label={`${option.name}${option._autoApplied ? ' (auto)' : ''}${priceLabel}`}
                          size="small"
                          variant={option._autoApplied ? 'filled' : 'outlined'}
                          sx={{
                            height: 22,
                            fontSize: '0.7rem',
                            fontWeight: option._autoApplied ? 600 : 500,
                            borderColor: option._autoApplied ? 'transparent' : 'rgba(0,0,0,0.1)',
                            color: option._autoApplied ? '#f59e0b' : '#6b7280',
                            background: option._autoApplied ? 'rgba(245, 158, 11, 0.1)' : 'transparent',
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
                {item.notes && (
                  <Box sx={{ ml: 5.5, display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.25 }}>
                    <NotesIcon sx={{ fontSize: '0.8rem', color: '#f59e0b', mt: 0.2 }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        color: '#92400e',
                        fontStyle: 'italic',
                        fontSize: '0.75rem',
                        lineHeight: 1.4,
                      }}
                    >
                      {item.notes}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, pt: 0.25 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 800,
                    color: '#1f2937',
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                  }}
                >
                  <CurrencyDisplay amount={calculateItemPrice(item) * item.quantity} showSymbolOnly />
                </Typography>
                <EditIcon sx={{ fontSize: '0.9rem', color: '#d1d5db' }} />
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Order Summary */}
      <Box sx={{
        borderRadius: 3,
        background: '#f8fafc',
        p: { xs: 2, sm: 2.5 },
        border: '1px solid rgba(0, 0, 0, 0.06)',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#6b7280' }}>Subtotal</Typography>
          <Typography variant="body2" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500 }}>
            <CurrencyDisplay amount={adjustedBreakdown.subtotal} showSymbolOnly />
          </Typography>
        </Box>

        {adjustedBreakdown.discountDetails.length > 0 && adjustedBreakdown.discountDetails.map((d) => (
          <Box key={d.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography variant="body2" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#16a34a' }}>
              {d.name}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500, color: '#16a34a' }}>
              -<CurrencyDisplay amount={d.amount} showSymbolOnly />
            </Typography>
          </Box>
        ))}

        {adjustedBreakdown.tax > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography variant="body2" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#6b7280' }}>
              Tax ({adjustedBreakdown.taxPercentage}%)
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500 }}>
              <CurrencyDisplay amount={adjustedBreakdown.tax} showSymbolOnly />
            </Typography>
          </Box>
        )}

        {adjustedBreakdown.serviceCharge > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography variant="body2" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#6b7280' }}>
              Service Charge ({adjustedBreakdown.serviceChargePercentage}%)
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500 }}>
              <CurrencyDisplay amount={adjustedBreakdown.serviceCharge} showSymbolOnly />
            </Typography>
          </Box>
        )}

        {adjustedBreakdown.platformFee > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography variant="body2" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#6b7280' }}>
              Platform Fee
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500 }}>
              <CurrencyDisplay amount={adjustedBreakdown.platformFee} showSymbolOnly />
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 1.5 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 800,
              color: '#1f2937',
              fontSize: '1.05rem',
            }}
          >
            Total
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 800,
              fontSize: '1.1rem',
              background: `linear-gradient(135deg, ${primaryColor || '#667eea'} 0%, ${getHoverColor(primaryColor || '#667eea', 'darken', 15)} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            <CurrencyDisplay amount={adjustedBreakdown.total} showSymbolOnly />
          </Typography>
        </Box>
      </Box>

      {editingItemIndex !== null && items[editingItemIndex] && (
        <MenuItemDetailsDialog
          open={editingItemIndex !== null}
          onClose={() => setEditingItemIndex(null)}
          item={items[editingItemIndex]}
          onAddToCart={() => {}}
          editMode
          editIndex={editingItemIndex}
          onUpdate={handleUpdateItem}
          initialQuantity={items[editingItemIndex].quantity}
          initialOptions={items[editingItemIndex].options}
          initialNotes={items[editingItemIndex].notes}
        />
      )}
    </Box>
  );

  const themedInputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 3,
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      fontSize: '0.95rem',
      transition: 'all 0.3s ease',
      '& fieldset': {
        borderColor: hexToRgba(primaryColor || '#667eea', 0.2),
        borderWidth: '1.5px',
      },
      '&:hover fieldset': {
        borderColor: hexToRgba(primaryColor || '#667eea', 0.4),
      },
      '&.Mui-focused fieldset': {
        borderColor: primaryColor || '#667eea',
        borderWidth: '2px',
      },
      '&:hover': {
        boxShadow: `0 4px 12px ${hexToRgba(primaryColor || '#667eea', 0.08)}`,
      },
      '&.Mui-focused': {
        boxShadow: `0 6px 20px ${hexToRgba(primaryColor || '#667eea', 0.12)}`,
      },
    },
    '& .MuiInputLabel-root': {
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      fontWeight: 500,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: primaryColor || '#667eea',
    },
  };

  const renderCustomerInfo = () => (
    <Box>
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontWeight: 800,
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          letterSpacing: '-0.02em',
          color: '#1f2937',
          mb: 1,
        }}
      >
        Almost there!
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          color: '#9ca3af',
          fontSize: { xs: '0.8rem', sm: '0.875rem' },
          mb: 3,
        }}
      >
        Let us know who the order is for
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <PersonIcon sx={{ fontSize: '1rem', color: primaryColor || '#667eea' }} />
            <Typography
              variant="body2"
              sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 700,
                color: '#374151',
                fontSize: '0.85rem',
              }}
            >
              Your Name *
            </Typography>
          </Box>
          <TextField
            required
            fullWidth
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Enter your name"
            sx={themedInputSx}
          />
        </Box>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <NotesIcon sx={{ fontSize: '1rem', color: '#9ca3af' }} />
            <Typography
              variant="body2"
              sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 700,
                color: '#374151',
                fontSize: '0.85rem',
              }}
            >
              Additional Notes
              <Typography component="span" sx={{ fontWeight: 400, color: '#9ca3af', ml: 0.5, fontSize: '0.8rem' }}>
                (Optional)
              </Typography>
            </Typography>
          </Box>
          <TextField
            fullWidth
            name="notes"
            multiline
            rows={3}
            value={formData.notes}
            onChange={handleInputChange}
            placeholder="Any allergies or special requests for the whole order?"
            sx={themedInputSx}
          />
        </Box>
      </Box>
    </Box>
  );

  const renderPaymentMethod = () => {
    // Use fresh merchant data if available, fallback to cart merchant data
    const currentMerchant = freshMerchant || merchant;

    // Show loading state if merchant settings are not yet loaded
    if (!currentMerchant?.settings) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Loading payment options...
          </Typography>
        </Box>
      );
    }

    // Build payment options dynamically based on merchant settings
    const paymentOptions = [];

    // Add cash payment if enabled
    if (currentMerchant.settings.enable_cash) {
      paymentOptions.push({
        value: 'cash',
        label: 'Cash',
        description: 'Pay with cash at the restaurant',
        hidden: false
      });
    }

    if (currentMerchant.settings.gcash_enabled) {
      paymentOptions.push({
        value: 'gcash',
        label: 'GCash',
        description: 'Pay via GCash and upload your receipt',
        hidden: false
      });
    }

    // If no payment options are available, show an error
    if (paymentOptions.length === 0) {
      return (
        <Box>
          <Typography variant="h6" gutterBottom>
            Payment Method
          </Typography>
          <Alert severity="error" sx={{ mt: 2 }}>
            No payment methods are currently available. Please contact the restaurant.
          </Alert>
        </Box>
      );
    }

    const paymentIcons = {
      cash: PaymentsIcon,
      gcash: PhoneAndroidIcon,
    };

    return (
      <Box>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 800,
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            letterSpacing: '-0.02em',
            color: '#1f2937',
            mb: 1,
          }}
        >
          How would you like to pay?
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            color: '#9ca3af',
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
            mb: 3,
          }}
        >
          Select your preferred payment method
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {paymentOptions.map((option) => {
            if (option.hidden) return null;
            const isSelected = resolvedPaymentMethod === option.value;
            const IconComp = paymentIcons[option.value] || CreditCardIcon;

            return (
              <Paper
                key={option.value}
                role="button"
                tabIndex={0}
                aria-pressed={isSelected}
                elevation={0}
                onClick={() => setFormData(prev => ({ ...prev, paymentMethod: option.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    if (e.key === ' ') e.preventDefault();
                    setFormData(prev => ({ ...prev, paymentMethod: option.value }));
                  }
                }}
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: 3,
                  cursor: 'pointer',
                  outline: 'none',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  border: isSelected
                    ? `2px solid ${primaryColor || '#667eea'}`
                    : '2px solid rgba(0, 0, 0, 0.06)',
                  background: isSelected
                    ? `linear-gradient(135deg, ${hexToRgba(primaryColor || '#667eea', 0.08)} 0%, ${hexToRgba(primaryColor || '#667eea', 0.03)} 100%)`
                    : '#fff',
                  boxShadow: isSelected
                    ? `0 4px 16px ${hexToRgba(primaryColor || '#667eea', 0.15)}`
                    : '0 1px 4px rgba(0, 0, 0, 0.04)',
                  '&:hover': {
                    borderColor: primaryColor || '#667eea',
                    boxShadow: `0 4px 16px ${hexToRgba(primaryColor || '#667eea', 0.12)}`,
                    transform: 'translateY(-2px)',
                  },
                  '&:focus-visible': {
                    borderColor: primaryColor || '#667eea',
                    boxShadow: `0 0 0 2px ${hexToRgba(primaryColor || '#667eea', 0.35)}`,
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      background: isSelected
                        ? `linear-gradient(135deg, ${primaryColor || '#667eea'} 0%, ${getHoverColor(primaryColor || '#667eea', 'darken', 15)} 100%)`
                        : 'rgba(0, 0, 0, 0.04)',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <IconComp sx={{
                      fontSize: 22,
                      color: isSelected ? '#fff' : '#9ca3af',
                      transition: 'color 0.3s ease',
                    }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontWeight: 700,
                        color: isSelected ? (primaryColor || '#667eea') : '#1f2937',
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        transition: 'color 0.3s ease',
                      }}
                    >
                      {option.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        color: '#9ca3af',
                        fontSize: { xs: '0.78rem', sm: '0.85rem' },
                      }}
                    >
                      {option.description}
                    </Typography>
                  </Box>
                  {isSelected && (
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${primaryColor || '#667eea'} 0%, ${getHoverColor(primaryColor || '#667eea', 'darken', 15)} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <CheckIcon sx={{ color: '#fff', fontSize: 16 }} />
                    </Box>
                  )}
                </Box>
              </Paper>
            );
          })}
        </Box>

        {paymentOptions.length === 1 && paymentOptions[0]?.value === 'cash' && (
          <Alert
            severity="info"
            sx={{
              mt: 2.5,
              borderRadius: 3,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            Only cash payments are accepted at this restaurant.
          </Alert>
        )}
      </Box>
    );
  };

  const handleOrderAgain = () => {
    // Clear cart to ensure fresh start for new order
    clearCart();
    // Close the dialog and allow customer to place another order
    handleClose();
  };

  const handleTrackOrder = () => {
    if (order && order.order_number) {
      handleClose();
      onTrackOrder?.(order.order_number);
    }
  };

  const handleCopyOrderNumber = async () => {
    if (order && order.order_number) {
      try {
        await navigator.clipboard.writeText(order.order_number);
        setCopied(true);
        // Reset the copied state after 2 seconds
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      } catch (err) {
        console.error('Failed to copy order number:', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = order.order_number;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          setCopied(true);
          setTimeout(() => {
            setCopied(false);
          }, 2000);
        } catch (fallbackErr) {
          console.error('Fallback copy failed:', fallbackErr);
        }
        document.body.removeChild(textArea);
      }
    }
  };

  // GCash countdown timer
  useEffect(() => {
    if (!gcashInfo?.expires_at || resolvedPaymentMethod !== 'gcash' || activeStep !== 4) return;
    if (gcashResult?.is_verified) return;

    const updateTimer = () => {
      const now = new Date().getTime();
      const expiry = new Date(gcashInfo.expires_at).getTime();
      const diff = Math.max(0, Math.floor((expiry - now) / 1000));
      setGcashTimeLeft(diff);
      if (diff <= 0) {
        setGcashExpired(true);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [gcashInfo?.expires_at, resolvedPaymentMethod, activeStep, gcashResult?.is_verified]);

  useEffect(() => {
    if (!gcashSelectedFile) {
      setGcashPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(gcashSelectedFile);
    setGcashPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [gcashSelectedFile]);

  const handleCopyGcashNumber = async () => {
    const number = gcashInfo?.mobile_number;
    if (!number) return;
    try {
      await navigator.clipboard.writeText(number);
      setGcashNumberCopied(true);
      setTimeout(() => setGcashNumberCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = number;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setGcashNumberCopied(true);
        setTimeout(() => setGcashNumberCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Failed to copy GCash number:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleGcashFileSelect = (file) => {
    setGcashSelectedFile(file);
    setGcashResult(null);
    setError(null);
  };

  const handleGcashUpload = async () => {
    const file = gcashSelectedFile;
    if (!gcashInfo?.payment_id) return;

    const validationError = validateReceiptFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setGcashUploading(true);
    setGcashResult(null);
    setError(null);

    try {
      const result = await paymentService.uploadGcashReceipt(gcashInfo.payment_id, file);
      setGcashResult(result);
      if (result.is_verified) {
        clearCart();
        clearGcashSession();
        setGcashSelectedFile(null);
        setActiveStep(4);
      } else {
        setError(result.notes || 'Receipt verification failed. Please check your receipt and try again.');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to upload receipt. Please try again.';
      setError(msg);
    } finally {
      setGcashUploading(false);
    }
  };

  const handleGcashRetry = async () => {
    if (!order?.id) return;
    setLoading(true);
    setError(null);

    try {
      const newInfo = await paymentService.retryGcashPayment(order.id);
      setGcashInfo(newInfo);
      setGcashResult(null);
      setGcashExpired(false);
      setGcashTimeLeft(null);
      setGcashSelectedFile(null);
      setGcashStage('payment');
      saveGcashSession({
        paymentId: newInfo.payment_id,
        merchantId: (freshMerchant || merchant)?.id,
        order: order && order.id
          ? { id: order.id, order_number: order.order_number, status: order.status }
          : null,
      });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Unable to retry payment.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const formatCountdown = (seconds) => {
    if (seconds === null) return '--:--';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const renderGcashPayment = () => {
    if (gcashResult?.is_verified) {
      return null;
    }

    const themeColor = primaryColor || '#667eea';
    const themeColorDark = getHoverColor(themeColor, 'darken', 15);
    const themeGradient = `linear-gradient(135deg, ${themeColor} 0%, ${themeColorDark} 100%)`;
    const themeGradientHover = `linear-gradient(135deg, ${getHoverColor(themeColor, 'darken', 5)} 0%, ${getHoverColor(themeColor, 'darken', 25)} 100%)`;

    return (
      <Box>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 800,
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            letterSpacing: '-0.02em',
            color: '#1f2937',
            mb: 1,
          }}
        >
          GCash Payment
        </Typography>

        {/* Timer */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 3,
          p: 1.5,
          borderRadius: 2,
          background: gcashExpired
            ? 'rgba(239, 68, 68, 0.08)'
            : gcashTimeLeft !== null && gcashTimeLeft <= 120
              ? 'rgba(245, 158, 11, 0.08)'
              : hexToRgba(themeColor, 0.06),
          border: gcashExpired
            ? '1px solid rgba(239, 68, 68, 0.2)'
            : `1px solid ${hexToRgba(themeColor, 0.15)}`,
        }}>
          <TimerIcon sx={{
            fontSize: 20,
            color: gcashExpired ? '#ef4444' : gcashTimeLeft <= 120 ? '#f59e0b' : themeColor,
          }} />
          <Typography variant="body2" sx={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 700,
            color: gcashExpired ? '#ef4444' : '#1f2937',
          }}>
            {gcashExpired ? 'Payment window expired' : `Time remaining: ${formatCountdown(gcashTimeLeft)}`}
          </Typography>
        </Box>

        {gcashExpired ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <ErrorOutlineIcon sx={{ fontSize: 48, color: '#ef4444', mb: 1.5 }} />
            <Typography variant="body1" sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 600,
              color: '#1f2937',
              mb: 1,
            }}>
              Payment window has expired
            </Typography>
            <Typography variant="body2" sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              color: '#6b7280',
              mb: 3,
            }}>
              {gcashInfo?.attempt_number >= gcashInfo?.max_attempts
                ? 'Maximum payment attempts reached. Please place a new order.'
                : 'You can try again with a new 10-minute window.'}
            </Typography>
            {gcashInfo?.attempt_number < gcashInfo?.max_attempts && (
              <Button
                variant="contained"
                startIcon={<ReplayIcon />}
                onClick={handleGcashRetry}
                disabled={loading}
                sx={{
                  borderRadius: '12px',
                  background: themeGradient,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  px: 4,
                  py: 1.5,
                }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Try Again'}
              </Button>
            )}
          </Box>
        ) : gcashStage === 'breakdown' ? (
          <>
            <Box sx={{
              borderRadius: 3,
              background: '#f8fafc',
              p: { xs: 2, sm: 2.5 },
              mb: 3,
              border: '1px solid rgba(0, 0, 0, 0.06)',
            }}>
              <Typography variant="subtitle2" sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 700,
                color: '#1f2937',
                mb: 2,
              }}>
                Bill Breakdown
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
                {getEffectiveItems().map((item, index) => (
                  <Box key={`${item.id}-${index}`} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#1f2937', fontWeight: 600 }}>
                        {item.quantity}x {item.name}
                      </Typography>
                      {item.options && item.options.length > 0 && (
                        <Typography variant="caption" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#9ca3af', display: 'block' }}>
                          {item.options.map(opt => opt.name).join(', ')}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, color: '#1f2937', flexShrink: 0 }}>
                      <CurrencyDisplay amount={calculateItemPrice(item) * item.quantity} showSymbolOnly />
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Divider sx={{ mb: 1.5 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#6b7280' }}>Subtotal</Typography>
                <Typography variant="body2" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500 }}>
                  <CurrencyDisplay amount={adjustedBreakdown.subtotal} showSymbolOnly />
                </Typography>
              </Box>

              {adjustedBreakdown.discountDetails.length > 0 && adjustedBreakdown.discountDetails.map((d) => (
                <Box key={d.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="body2" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#16a34a' }}>
                    {d.name}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500, color: '#16a34a' }}>
                    -<CurrencyDisplay amount={d.amount} showSymbolOnly />
                  </Typography>
                </Box>
              ))}

              {adjustedBreakdown.tax > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="body2" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#6b7280' }}>
                    Tax ({adjustedBreakdown.taxPercentage}%)
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500 }}>
                    <CurrencyDisplay amount={adjustedBreakdown.tax} showSymbolOnly />
                  </Typography>
                </Box>
              )}

              {adjustedBreakdown.serviceCharge > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="body2" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#6b7280' }}>
                    Service Charge ({adjustedBreakdown.serviceChargePercentage}%)
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500 }}>
                    <CurrencyDisplay amount={adjustedBreakdown.serviceCharge} showSymbolOnly />
                  </Typography>
                </Box>
              )}

              {adjustedBreakdown.platformFee > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="body2" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#6b7280' }}>
                    Platform Fee
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 500 }}>
                    <CurrencyDisplay amount={adjustedBreakdown.platformFee} showSymbolOnly />
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 1.5 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 800,
                  color: '#1f2937',
                  fontSize: '1.05rem',
                }}>
                  Total
                </Typography>
                <Typography variant="subtitle1" sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 800,
                  fontSize: '1.1rem',
                  color: themeColor,
                }}>
                  <CurrencyDisplay amount={parseFloat(gcashInfo?.order_total || adjustedBreakdown.total)} showSymbolOnly />
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              fullWidth
              onClick={() => setGcashStage('payment')}
              sx={{
                borderRadius: '14px',
                background: themeGradient,
                textTransform: 'none',
                fontWeight: 700,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                py: 1.5,
                fontSize: '0.95rem',
                boxShadow: `0 6px 20px ${hexToRgba(themeColor, 0.3)}`,
                '&:hover': {
                  background: themeGradientHover,
                },
              }}
            >
              Proceed to Payment
            </Button>
          </>
        ) : (
          <>
            {/* GCash payment details */}
            <Paper elevation={0} sx={{
              p: { xs: 2, sm: 3 },
              mb: 3,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${hexToRgba(themeColor, 0.04)} 0%, ${hexToRgba(themeColorDark, 0.04)} 100%)`,
              border: `1px solid ${hexToRgba(themeColor, 0.12)}`,
            }}>
              <Typography variant="subtitle2" sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 700,
                color: themeColor,
                mb: 2,
              }}>
                Send payment to:
              </Typography>

              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="caption" sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontSize: '0.65rem',
                }}>
                  Send to Mobile Number
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 0.5 }}>
                  <Typography variant="h5" sx={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 900,
                    color: '#1f2937',
                    letterSpacing: '2px',
                    fontSize: { xs: '1.4rem', sm: '1.6rem' },
                  }}>
                    {gcashInfo?.mobile_number}
                  </Typography>
                  <IconButton
                    onClick={handleCopyGcashNumber}
                    size="small"
                    aria-label="Copy GCash number"
                    title={gcashNumberCopied ? 'Copied!' : 'Copy number'}
                    sx={{
                      color: themeColor,
                      '&:hover': { backgroundColor: hexToRgba(themeColor, 0.08) },
                    }}
                  >
                    {gcashNumberCopied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </Box>
                {gcashNumberCopied && (
                  <Typography variant="caption" sx={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    color: '#16a34a',
                    display: 'block',
                    mt: 0.5,
                    fontWeight: 600,
                  }}>
                    Copied to clipboard!
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  color: '#6b7280',
                }}>
                  Amount to Send
                </Typography>
                <Typography variant="body1" sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 800,
                  color: themeColor,
                  fontSize: '1.15rem',
                }}>
                  <CurrencyDisplay amount={parseFloat(gcashInfo?.order_total || 0)} showSymbolOnly />
                </Typography>
              </Box>
            </Paper>

            {/* Upload receipt */}
            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  borderRadius: '12px',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                }}
              >
                {error}
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700,
              color: '#1f2937',
              mb: 0.5,
            }}>
              Upload GCash Receipt Screenshot
            </Typography>

            <Typography variant="caption" sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              color: '#6b7280',
              display: 'block',
              mb: 1.5,
            }}>
              Paid from a bank or another e-wallet app? A screenshot of the
              completed InstaPay transfer to this GCash number works too.
            </Typography>

            {gcashUploading ? (
              <Box sx={{
                textAlign: 'center',
                py: 4,
                borderRadius: 3,
                border: `2px dashed ${hexToRgba(themeColor, 0.3)}`,
                background: hexToRgba(themeColor, 0.03),
              }}>
                <CircularProgress size={36} sx={{ color: themeColor, mb: 1.5 }} />
                <Typography variant="body2" sx={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  color: themeColor,
                  fontWeight: 600,
                }}>
                  Verifying your receipt...
                </Typography>
              </Box>
            ) : (
              <>
                <Box
                  component="label"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: gcashSelectedFile ? 'flex-start' : 'center',
                    gap: 1.5,
                    width: '100%',
                    minHeight: 88,
                    p: gcashSelectedFile ? 1.5 : 2,
                    borderRadius: '14px',
                    border: `2px dashed ${hexToRgba(themeColor, 0.3)}`,
                    background: gcashSelectedFile ? hexToRgba(themeColor, 0.05) : 'transparent',
                    cursor: gcashExpired ? 'not-allowed' : 'pointer',
                    opacity: gcashExpired ? 0.6 : 1,
                    transition: 'all 0.2s ease',
                    '&:hover': gcashExpired ? {} : {
                      borderColor: themeColor,
                      backgroundColor: hexToRgba(themeColor, 0.06),
                    },
                  }}
                >
                  {gcashSelectedFile ? (
                    <>
                      {gcashPreviewUrl && (
                        <Box
                          component="img"
                          src={gcashPreviewUrl}
                          alt="GCash receipt preview"
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 1.5,
                            objectFit: 'cover',
                            flexShrink: 0,
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                          }}
                        />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                          <CheckIcon sx={{ fontSize: 18, color: '#16a34a', flexShrink: 0 }} />
                          <Typography variant="body2" noWrap sx={{
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            color: '#1f2937',
                            fontWeight: 600,
                            minWidth: 0,
                          }}>
                            {gcashSelectedFile.name}
                          </Typography>
                        </Box>
                        <Typography variant="caption" sx={{
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          color: themeColor,
                          fontWeight: 600,
                          display: 'block',
                          mt: 0.25,
                        }}>
                          Tap to choose a different screenshot
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <>
                      <CloudUploadIcon sx={{ color: themeColor }} />
                      <Typography variant="body2" sx={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        color: themeColor,
                        fontWeight: 700,
                        fontSize: '0.95rem',
                      }}>
                        Tap to Select Screenshot
                      </Typography>
                    </>
                  )}
                  <input
                    type="file"
                    hidden
                    accept="image/jpeg,image/png,image/webp"
                    disabled={gcashExpired}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleGcashFileSelect(file);
                      e.target.value = '';
                    }}
                  />
                </Box>

                {gcashSelectedFile && (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleGcashUpload}
                    disabled={gcashExpired}
                    sx={{
                      mt: 1.5,
                      borderRadius: '14px',
                      background: themeGradient,
                      textTransform: 'none',
                      fontWeight: 700,
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      py: 1.5,
                      fontSize: '0.95rem',
                      boxShadow: `0 6px 20px ${hexToRgba(themeColor, 0.3)}`,
                      '&:hover': {
                        background: themeGradientHover,
                      },
                    }}
                  >
                    Process Payment
                  </Button>
                )}
              </>
            )}
          </>
        )}
      </Box>
    );
  };

  const renderConfirmation = () => (
    <Box>
      {order ? (
        <Box textAlign="center" my={{ xs: 1, sm: 2 }}>
          <Box
            sx={{
              width: { xs: 56, sm: 72 },
              height: { xs: 56, sm: 72 },
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${primaryColor || '#667eea'} 0%, ${getHoverColor(primaryColor || '#667eea', 'darken', 15)} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2.5,
              boxShadow: `0 8px 24px ${hexToRgba(primaryColor || '#667eea', 0.3)}`,
            }}
          >
            <CheckIcon sx={{ color: '#fff', fontSize: { xs: 28, sm: 36 } }} />
          </Box>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: { xs: '1.5rem', sm: '2rem' },
              fontWeight: 800,
              letterSpacing: '-0.02em',
              mb: { xs: 1, sm: 1.5 },
              background: `linear-gradient(135deg, ${primaryColor || '#667eea'} 0%, ${getHoverColor(primaryColor || '#667eea', 'darken', 15)} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Order placed!
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 3 },
              mb: { xs: 2.5, sm: 3 },
              mx: { xs: 0, sm: 'auto' },
              maxWidth: { xs: '100%', sm: 400 },
              background: `linear-gradient(135deg, ${primaryColor || '#667eea'} 0%, ${getHoverColor(primaryColor || '#667eea', 'darken', 15)} 100%)`,
              color: '#fff',
              borderRadius: 4,
              boxShadow: `0 12px 32px ${hexToRgba(primaryColor || '#667eea', 0.35)}`,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: '0.8rem',
                fontWeight: 600,
                mb: 1.5,
                color: 'rgba(255, 255, 255, 0.85)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Order Number
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                width: '100%',
                minWidth: 0,
                background: 'rgba(255, 255, 255, 0.15)',
                borderRadius: 3,
                py: 1.5,
                px: { xs: 1, sm: 2 },
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  textAlign: 'center',
                }}
              >
                <Typography
                  component="div"
                  variant="h4"
                  sx={{
                    display: 'block',
                    fontFamily: "'Plus Jakarta Sans', ui-monospace, monospace",
                    fontSize: { xs: 'clamp(0.85rem, 3.6vw, 1.5rem)', sm: '1.75rem' },
                    fontWeight: 800,
                    letterSpacing: { xs: 0.2, sm: 1.5 },
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'clip',
                  }}
                >
                  {order.order_number}
                </Typography>
              </Box>
              <IconButton
                onClick={handleCopyOrderNumber}
                size="small"
                sx={{
                  color: '#fff',
                  flexShrink: 0,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  }
                }}
                title={copied ? "Copied!" : "Copy order number"}
              >
                {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
              </IconButton>
            </Box>
            {copied && (
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  textAlign: 'center',
                  opacity: 0.9,
                  fontSize: '0.75rem',
                }}
              >
                Copied to clipboard!
              </Typography>
            )}
          </Paper>

          <Typography
            variant="body1"
            paragraph
            sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: { xs: '0.9rem', sm: '1rem' },
              mb: { xs: 2, sm: 3 },
              px: { xs: 1, sm: 0 },
              color: '#6b7280',
            }}
          >
            {order.status === 'preparing'
              ? 'Your order has been accepted and is now being prepared.'
              : 'Your order has been received and is being processed.'}
          </Typography>

          {/* Cash Payment Instruction - only show when order is pending (not auto-accepted) */}
          {resolvedPaymentMethod === 'cash' && order.status !== 'preparing' && (
            <Box
              sx={{
                mb: { xs: 2, sm: 3 },
                mx: { xs: 0, sm: 'auto' },
                maxWidth: { xs: '100%', sm: 400 },
                borderRadius: 3,
                backgroundColor: '#fffbeb',
                border: '1.5px solid #fbbf24',
                p: { xs: 2, sm: 2.5 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <PaymentsIcon sx={{ fontSize: '1.25rem', color: '#f59e0b', mt: 0.25, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: { xs: '0.85rem', sm: '0.9rem' },
                      mb: 0.5,
                      color: '#92400e',
                      fontWeight: 700,
                      lineHeight: 1.4,
                    }}
                  >
                    Wait for staff to collect payment
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: { xs: '0.78rem', sm: '0.8rem' },
                      color: '#a16207',
                      lineHeight: 1.5,
                    }}
                  >
                    A staff member will come to you to collect your payment. Show your order number to complete it.
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          <Typography
            variant="body2"
            paragraph
            sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              px: { xs: 1, sm: 0 },
              color: '#9ca3af',
            }}
          >
            Please keep your order number for reference.
          </Typography>

        </Box>
      ) : (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={isGcashPaymentActive() ? undefined : handleClose}
      TransitionProps={{ onExited: resetDialogState }}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? '20px 20px 0 0' : '24px',
          boxShadow: '0 30px 60px rgba(0, 0, 0, 0.3)',
          margin: isMobile ? 0 : 2,
          maxHeight: { xs: '100vh', sm: '90vh' },
          ...(isMobile && {
            position: 'fixed',
            bottom: 0,
            top: 'auto',
            maxHeight: '95vh',
          }),
          overflow: 'hidden',
        }
      }}
    >
      <DialogTitle
        sx={{
          pb: 0,
          pt: 0,
          px: 0,
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${primaryColor || '#667eea'} 0%, ${getHoverColor(primaryColor || '#667eea', 'darken', 15)} 100%)`,
            px: { xs: 2.5, sm: 3.5 },
            py: { xs: 2, sm: 2.5 },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: '#fff',
              fontSize: { xs: '1.35rem', sm: '1.75rem' },
            }}
          >
            Checkout
          </Typography>
          {activeStep === 4 && !isGcashPaymentActive() ? (
            <IconButton
              edge="end"
              onClick={handleClose}
              aria-label="close"
              size={isMobile ? "small" : "medium"}
              sx={{
                color: '#fff',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'rotate(90deg) scale(1.1)',
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          ) : activeStep !== 4 ? (
            <Typography
              variant="body2"
              sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                color: 'rgba(255, 255, 255, 0.85)',
                fontWeight: 600,
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
              }}
            >
              {items.length} {items.length === 1 ? 'item' : 'items'}
            </Typography>
          ) : null}
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 3 },
          pt: { xs: 3, sm: 4 },
          overflow: 'auto',
        }}
      >
        {gcashRestoring ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
            <CircularProgress size={36} sx={{ color: primaryColor || '#667eea' }} />
            <Typography variant="body2" sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              color: '#6b7280',
              fontWeight: 600,
            }}>
              Restoring your payment session...
            </Typography>
          </Box>
        ) : isMobile ? (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Step {activeStep + 1} of {steps.length}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: primaryColor || '#667eea',
                }}
              >
                {Math.round(((activeStep + 1) / steps.length) * 100)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={((activeStep + 1) / steps.length) * 100}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  background: `linear-gradient(90deg, ${primaryColor || '#667eea'} 0%, ${getHoverColor(primaryColor || '#667eea', 'darken', 10)} 100%)`,
                },
              }}
            />
            <Typography
              variant="subtitle2"
              sx={{
                mt: 1.5,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: '0.875rem',
                fontWeight: 700,
                color: '#1f2937',
              }}
            >
              {steps[activeStep]}
            </Typography>
          </Box>
        ) : (
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              mb: 4,
              width: '100%',
              py: 0.5,
              '& .MuiStep-root': {
                flex: '1 1 0',
                minWidth: 0,
                px: { sm: 0.25, md: 0.75 },
              },
              '& .MuiStepLabel-label': {
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: { sm: '0.72rem', md: '0.78rem' },
                fontWeight: 600,
                lineHeight: 1.35,
                textAlign: 'center',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              },
              '& .MuiStepLabel-label.Mui-active': {
                color: primaryColor || '#667eea',
                fontWeight: 700,
              },
              '& .MuiStepLabel-label.Mui-completed': {
                color: primaryColor || '#667eea',
              },
              '& .MuiStepIcon-root.Mui-active': {
                color: primaryColor || '#667eea',
              },
              '& .MuiStepIcon-root.Mui-completed': {
                color: primaryColor || '#667eea',
              },
              '& .MuiStepConnector-line': {
                borderColor: hexToRgba(primaryColor || '#667eea', 0.2),
                borderTopWidth: 2,
              },
              '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
                borderColor: primaryColor || '#667eea',
              },
              '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
                borderColor: primaryColor || '#667eea',
              },
            }}
            orientation="horizontal"
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {!gcashRestoring && error && !(
          activeStep === 4 &&
          resolvedPaymentMethod === 'gcash' &&
          gcashStage === 'payment' &&
          gcashInfo &&
          !gcashResult?.is_verified
        ) && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!gcashRestoring && (
          <Box sx={{ mt: 3 }}>
            {renderStepContent()}
          </Box>
        )}
      </DialogContent>

      {!gcashRestoring && <DialogActions sx={{
        justifyContent: activeStep === 4 ? 'center' : 'space-between',
        p: { xs: 2, sm: 3 },
        gap: { xs: 1.5, sm: 2 },
        flexDirection: { xs: 'column-reverse', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        borderTop: '1px solid rgba(0, 0, 0, 0.06)',
        '& > :not(style) ~ :not(style)': {
          ml: { xs: 0, sm: undefined },
        },
      }}>
        {activeStep === 4 && resolvedPaymentMethod === 'gcash' && gcashInfo && !gcashResult?.is_verified ? (
          <Box sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5,
          }}>
            <Typography variant="caption" sx={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              color: '#9ca3af',
            }}>
              {gcashExpired
                ? 'Your payment window has expired'
                : gcashStage === 'breakdown'
                  ? 'Review your bill, then proceed to payment'
                  : 'Select your GCash receipt screenshot, then tap Process Payment'}
            </Typography>
            <Button
              type="button"
              variant="text"
              onClick={handleCancelGcash}
              disabled={gcashUploading}
              sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 600,
                fontSize: '0.8rem',
                textTransform: 'none',
                color: '#ef4444',
                '&:hover': {
                  backgroundColor: 'rgba(239, 68, 68, 0.06)',
                },
                '&.Mui-disabled': {
                  color: 'rgba(239, 68, 68, 0.4)',
                },
              }}
            >
              Cancel Payment
            </Button>
          </Box>
        ) : activeStep === 4 ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1.5,
            width: '100%',
            justifyContent: 'center',
          }}>
            {order && order.order_number && (
              <Button
                variant="outlined"
                onClick={handleTrackOrder}
                sx={{
                  flex: 1,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  borderRadius: 3,
                  px: { xs: 2, sm: 4 },
                  py: 1.25,
                  fontWeight: 700,
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  textTransform: 'none',
                  borderColor: hexToRgba(primaryColor || '#667eea', 0.4),
                  color: primaryColor || '#667eea',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: primaryColor || '#667eea',
                    backgroundColor: hexToRgba(primaryColor || '#667eea', 0.06),
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Track Order
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleOrderAgain}
              sx={{
                flex: 1,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                borderRadius: 3,
                px: { xs: 2, sm: 4 },
                py: 1.25,
                fontWeight: 700,
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                textTransform: 'none',
                background: `linear-gradient(135deg, ${primaryColor || '#667eea'} 0%, ${getHoverColor(primaryColor || '#667eea', 'darken', 15)} 100%)`,
                boxShadow: `0 6px 20px ${hexToRgba(primaryColor || '#667eea', 0.3)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 10px 28px ${hexToRgba(primaryColor || '#667eea', 0.4)}`,
                },
              }}
            >
              Done
            </Button>
          </Box>
        ) : (
          <>
            <Button
              disabled={loading}
              onClick={handleBack}
              fullWidth={isMobile}
              sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                borderRadius: 3,
                px: { xs: 3, sm: 4 },
                py: { xs: 1.25, sm: 1.5 },
                fontWeight: 600,
                fontSize: { xs: '0.9rem', sm: '0.95rem' },
                textTransform: 'none',
                color: '#6b7280',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  transform: 'translateY(-1px)',
                }
              }}
            >
              Back
            </Button>

            <Button
              variant="contained"
              onClick={handleNext}
              disabled={isNextDisabled()}
              fullWidth={isMobile}
              sx={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                borderRadius: 3,
                px: { xs: 3, sm: 5 },
                py: { xs: 1.5, sm: 1.75 },
                fontWeight: 700,
                fontSize: { xs: '0.95rem', sm: '1.05rem' },
                textTransform: 'none',
                background: `linear-gradient(135deg, ${primaryColor || '#667eea'} 0%, ${getHoverColor(primaryColor || '#667eea', 'darken', 15)} 100%)`,
                boxShadow: `0 8px 25px ${hexToRgba(primaryColor || '#667eea', 0.35)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  background: `linear-gradient(135deg, ${getHoverColor(primaryColor || '#667eea', 'darken', 5)} 0%, ${getHoverColor(primaryColor || '#667eea', 'darken', 20)} 100%)`,
                  boxShadow: `0 12px 35px ${hexToRgba(primaryColor || '#667eea', 0.45)}`,
                },
                '&.Mui-disabled': {
                  background: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                  boxShadow: 'none',
                },
                minWidth: { xs: 0, sm: 160 },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : activeStep === 3 ? (
                resolvedPaymentMethod === 'gcash' ? 'Proceed to GCash Payment' : 'Place Order'
              ) : (
                'Next'
              )}
            </Button>
          </>
        )}
      </DialogActions>}
    </Dialog>
  );
};

export default CheckoutDialog;
